import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Typography,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  DEFAULT_EXCEPTION_STATUSES,
  FALLBACK_ORDER_STATUSES,
  buildDefaultExceptionRange,
  buildExceptionsRequestBody,
  getDisplayTimeZone,
  resolveDefaultExceptionStatuses,
} from "../../utils/orderExceptionsDefaults";
import { orderExceptionsService } from "../../utils/services/orderExceptionsService";

const ExceptionsParamsModal = ({
  open,
  onClose,
  onGenerate,
  submitting,
  error,
}) => {
  const [availableStatuses, setAvailableStatuses] = useState(
    FALLBACK_ORDER_STATUSES,
  );
  const [selectedStatuses, setSelectedStatuses] = useState(
    DEFAULT_EXCEPTION_STATUSES,
  );
  const [{ start, end }, setRange] = useState(() =>
    buildDefaultExceptionRange(dayjs),
  );

  useEffect(() => {
    if (!open) return;
    setRange(buildDefaultExceptionRange(dayjs));
    setSelectedStatuses(DEFAULT_EXCEPTION_STATUSES);
    let cancelled = false;
    orderExceptionsService
      .options()
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data?.available_statuses) && data.available_statuses.length) {
          setAvailableStatuses(data.available_statuses);
        }
        setSelectedStatuses(resolveDefaultExceptionStatuses(data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const resetDefaults = () => {
    setSelectedStatuses(DEFAULT_EXCEPTION_STATUSES);
    setRange(buildDefaultExceptionRange(dayjs));
  };

  const toggleStatus = (status) => {
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status],
    );
  };

  const invalidRange = !start || !end || start.valueOf() > end.valueOf();
  const missingStatus = selectedStatuses.length === 0;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, position: "relative" } }}
    >
      {submitting && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
        />
      )}
      <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
        Exceptions report
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Identify unfulfilled orders and whether current on-hand inventory can
          satisfy them. Inventory analysis is informational if you include
          closed statuses such as Delivered or Cancelled.
        </Alert>
        <Typography
          variant="subtitle2"
          sx={{ fontFamily: "Poppins", fontWeight: 600, mb: 1 }}
        >
          Order status
        </Typography>
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            mb: 2,
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {availableStatuses.map((status) => (
            <FormControlLabel
              key={status}
              control={
                <Checkbox
                  checked={selectedStatuses.includes(status)}
                  onChange={() => toggleStatus(status)}
                  disabled={submitting}
                />
              }
              label={status}
              sx={{ fontFamily: "Poppins" }}
            />
          ))}
        </FormGroup>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "grid", gap: 2 }}>
            <DateTimePicker
              label="Start date/time"
              value={start}
              onChange={(value) => setRange((current) => ({ ...current, start: value }))}
              disabled={submitting}
              ampm={false}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DateTimePicker
              label="End date/time"
              value={end}
              onChange={(value) => setRange((current) => ({ ...current, end: value }))}
              disabled={submitting}
              ampm={false}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1.5, color: "text.secondary" }}
        >
          Times are in {getDisplayTimeZone()}. Defaults: 7 days ago at 00:00
          through yesterday at 15:00.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={submitting} onClick={resetDefaults}>
          Reset
        </Button>
        <Button
          variant="contained"
          disabled={submitting || invalidRange || missingStatus}
          onClick={() =>
            onGenerate(
              buildExceptionsRequestBody({
                statuses: selectedStatuses,
                start,
                end,
              }),
            )
          }
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" thickness={5} />
            ) : null
          }
        >
          {submitting ? "Generating..." : "Generate Report"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExceptionsParamsModal;
